(function () {
  'use strict';

  if (customElements.get('category-showcase')) return;

  const MOBILE_QUERY = '(max-width: 767px)';
  const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

  class CategoryShowcase extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.abortController = new AbortController();
      this.items = Array.from(this.querySelectorAll('[data-category-showcase-item]'));
      this.itemMedia = this.items.map((item) => item.querySelector('.category-showcase__media'));
      // Per-frame variables are written on the elements that read them, never on the item, so a
      // scroll frame re-styles a few nodes instead of every product card inside the item.
      this.itemPanels = this.items.map((item) => Array.from(item.querySelectorAll('.banner-sticky-scrolling__product-panel')));
      this.buttons = Array.from(this.querySelectorAll('[data-category-showcase-nav]'));
      this.track = this.querySelector('[data-category-showcase-track]');
      this.stage = this.querySelector('[data-category-showcase-stage]');
      this.rail = this.querySelector('[data-category-showcase-nav-rail]');
      this.sidebarScroller = this.querySelector('.category-showcase__nav');
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMedia = window.matchMedia(REDUCED_QUERY);
      this.activeIndex = -1;
      this.isNearViewport = false;
      this.frame = 0;
      this.flowOffsets = [];

      if (!this.items.length || !this.track) return;

      this.syncNavigationLabels();

      this.onScroll = this.onScroll.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onEnvironmentChange = this.onEnvironmentChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.scrollToIndex(Number(button.dataset.index)), {
          signal: this.abortController.signal
        });
      });

      window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.abortController.signal });
      window.addEventListener('resize', this.onEnvironmentChange, { passive: true, signal: this.abortController.signal });
      this.addEventListener('shopify:block:select', this.onBlockSelect, { signal: this.abortController.signal });
      this.mobileMedia.addEventListener('change', this.onEnvironmentChange, { signal: this.abortController.signal });
      this.reducedMedia.addEventListener('change', this.onEnvironmentChange, { signal: this.abortController.signal });

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver(this.onViewportChange, { rootMargin: '100% 0px 100% 0px' });
        this.viewportObserver.observe(this);
      } else {
        this.isNearViewport = true;
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate(true));
        this.resizeObserver.observe(this);
        this.items.forEach((item) => this.resizeObserver.observe(item));
        if (this.rail) this.resizeObserver.observe(this.rail);
      }

      this.resolveStyle();
      this.updateMeasurements();
      this.classList.add('is-enhanced');
      this.setActive(0, false);
      this.scheduleUpdate(true);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.viewportObserver) this.viewportObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.pauseVideos();
      this._connected = false;
    }

    syncNavigationLabels() {
      this.buttons.forEach((button, index) => {
        const labelTarget = button.querySelector('[data-category-showcase-nav-label]');
        const renderedLabel = labelTarget ? labelTarget.textContent.trim() : '';
        const itemLabel = this.items[index] ? (this.items[index].dataset.categoryLabel || '').trim() : '';
        const label = itemLabel || renderedLabel;
        if (labelTarget) labelTarget.textContent = label;
        button.hidden = !label;
      });
    }

    onViewportChange(entries) {
      this.isNearViewport = entries.some((entry) => entry.isIntersecting);
      if (this.isNearViewport) {
        this.syncVideos();
        this.scheduleUpdate(true);
      } else {
        this.pauseVideos();
        this.syncProductTitleReveals(-1, false);
      }
    }

    onEnvironmentChange() {
      this.resolveStyle();
      this.scheduleUpdate(true);
    }

    onScroll() {
      if (this.isNearViewport) this.scheduleUpdate(false);
    }

    onBlockSelect(event) {
      const item = event.target.closest('[data-category-showcase-item]');
      if (!item || !this.contains(item)) return;
      const index = this.items.indexOf(item);
      if (index < 0) return;
      this.setActive(index, true);
      this.scrollToIndex(index, true);
    }

    resolveStyle() {
      const requested = this.mobileMedia.matches ? this.dataset.mobileStyle : this.dataset.desktopStyle;
      const style = this.reducedMedia.matches || this.items.length < 2 ? 'normal' : (requested || 'normal');
      this.dataset.resolvedStyle = style;
      if (style !== 'wipe') {
        this.itemMedia.forEach((media) => {
          if (!media) return;
          media.style.removeProperty('--category-showcase-wipe-inset');
          media.style.removeProperty('--category-showcase-wipe-media-y');
        });
        this.track.style.removeProperty('--category-showcase-wipe-track-height');
        if (this.stage) this.stage.style.removeProperty('--category-showcase-stage-height');
      }

      if (this.reducedMedia.matches) this.clearProductMotion();
    }

    scheduleUpdate(measure) {
      this.needsMeasure = this.needsMeasure || measure;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsMeasure) {
          this.needsMeasure = false;
          this.updateMeasurements();
        }
        this.updateFromScroll();
      });
    }

    updateMeasurements() {
      const usesOverlayNavigation = this.dataset.navigationPlacement === 'overlay-center';
      const railHeight = this.mobileMedia.matches && !usesOverlayNavigation && this.rail
        ? Math.ceil(this.rail.getBoundingClientRect().height)
        : 0;
      this.style.setProperty('--category-showcase-mobile-nav-height', `${railHeight}px`);

      const style = this.dataset.resolvedStyle;
      const stickyTop = this.getStickyTop();
      const availableHeight = Math.max(1, window.innerHeight - stickyTop);
      this.wipeStep = Math.max(1, Math.round(window.innerHeight));
      this.style.setProperty('--category-showcase-wipe-step', `${this.wipeStep}px`);

      if (style === 'wipe' && this.stage) {
        const wasEnhanced = this.classList.contains('is-enhanced');
        if (wasEnhanced) this.classList.remove('is-enhanced');
        const stageHeight = Math.max(
          1,
          ...this.items.map((item) => Math.ceil(item.querySelector('.category-showcase__card')?.getBoundingClientRect().height || 0))
        );
        if (wasEnhanced) this.classList.add('is-enhanced');

        const transitionRange = this.wipeStep * Math.max(0, this.items.length - 1);
        const trackHeight = stageHeight + transitionRange + availableHeight;
        this.stage.style.setProperty('--category-showcase-stage-height', `${stageHeight}px`);
        this.track.style.setProperty('--category-showcase-wipe-track-height', `${Math.ceil(trackHeight)}px`);
        this.flowOffsets = this.items.map((item, index) => index * this.wipeStep);
        return;
      }

      this.track.style.removeProperty('--category-showcase-wipe-track-height');
      if (this.stage) this.stage.style.removeProperty('--category-showcase-stage-height');

      const rowGap = parseFloat(getComputedStyle(this.track).rowGap) || 0;
      let nextOffset = 0;
      this.flowOffsets = this.items.map((item) => {
        const itemStyle = getComputedStyle(item);
        nextOffset += parseFloat(itemStyle.marginTop) || 0;
        const currentOffset = nextOffset;
        nextOffset += item.offsetHeight + (parseFloat(itemStyle.marginBottom) || 0) + rowGap;
        return currentOffset;
      });
    }

    getStickyTop() {
      const rootStyles = getComputedStyle(document.documentElement);
      const headerHeight = parseFloat(rootStyles.getPropertyValue('--axiom-header-height')) || 0;
      const usesOverlayNavigation = this.dataset.navigationPlacement === 'overlay-center';
      const railHeight = this.mobileMedia.matches && !usesOverlayNavigation && this.rail
        ? this.rail.getBoundingClientRect().height
        : 0;
      return headerHeight + railHeight + (this.mobileMedia.matches ? 12 : 16);
    }

    // Runs once per frame while scrolling. All geometry is read first and all styles are written
    // afterwards, so the frame never forces a second style/layout pass.
    updateFromScroll() {
      if (!this.isNearViewport && this.viewportObserver) return;

      const style = this.dataset.resolvedStyle;
      const stickyTop = this.getStickyTop();
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const trackTop = this.track.getBoundingClientRect().top + scrollY;
      const rects = this.items.map((item) => item.getBoundingClientRect());

      let activeIndex = 0;
      let continuousIndex = null;
      let wipeProgress = null;

      if (style === 'wipe') {
        const transitionDistance = Math.max(1, this.wipeStep || viewportHeight);
        const transitionRange = transitionDistance * Math.max(0, this.items.length - 1);
        const scrolled = Math.max(0, Math.min(transitionRange, scrollY + stickyTop - trackTop));
        continuousIndex = scrolled / transitionDistance;
        activeIndex = Math.min(this.items.length - 1, Math.round(continuousIndex));
        wipeProgress = this.items.map((item, index) => {
          if (index === 0) return 1;
          let progress = Math.max(0, Math.min(1, (scrolled - ((index - 1) * transitionDistance)) / transitionDistance));
          if (progress < 0.001) progress = 0;
          if (progress > 0.999) progress = 1;
          return progress;
        });
      } else {
        let closestDistance = Number.POSITIVE_INFINITY;
        const useCenterMarker = style === 'normal' && this.dataset.activeMarker === 'center';
        const activeMarker = useCenterMarker ? viewportHeight / 2 : stickyTop;

        rects.forEach((rect, index) => {
          const itemMarker = useCenterMarker ? rect.top + (rect.height / 2) : rect.top;
          const distance = Math.abs(itemMarker - activeMarker);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });

        if (style === 'overlap') {
          const marker = scrollY + stickyTop + 1;
          continuousIndex = this.getContinuousIndex(Math.max(0, marker - trackTop));
          activeIndex = 0;
          this.items.forEach((item, index) => {
            if (trackTop + this.flowOffsets[index] <= marker) activeIndex = index;
          });
        }
      }

      const canReveal = this.canRevealItem(rects[activeIndex], stickyTop, viewportHeight);

      // Write phase.
      if (wipeProgress) {
        this.itemMedia.forEach((media, index) => {
          if (!media) return;
          const progress = wipeProgress[index];
          media.style.setProperty('--category-showcase-wipe-inset', `${((1 - progress) * 100).toFixed(3)}%`);
          media.style.setProperty('--category-showcase-wipe-media-y', `${((1 - progress) * 8).toFixed(3)}%`);
        });
      }
      this.updateProductMotion(continuousIndex, stickyTop, rects, viewportHeight);
      this.setActive(activeIndex, true, canReveal);
    }

    canRevealItem(rect, stickyTop, viewportHeight) {
      return Boolean(this.isNearViewport && rect && rect.bottom > stickyTop && rect.top < viewportHeight * 0.78);
    }

    getContinuousIndex(trackProgress) {
      if (this.items.length < 2) return 0;
      const lastIndex = this.items.length - 1;
      if (trackProgress >= this.flowOffsets[lastIndex]) return lastIndex;

      for (let index = 0; index < lastIndex; index += 1) {
        const start = this.flowOffsets[index];
        const end = this.flowOffsets[index + 1];
        if (trackProgress > end) continue;
        const progress = Math.max(0, Math.min(1, (trackProgress - start) / Math.max(1, end - start)));
        return index + progress;
      }

      return 0;
    }

    // Write-only: positions come from the rects measured in updateFromScroll.
    updateProductMotion(continuousIndex, stickyTop, rects, viewportHeight) {
      if (this.reducedMedia.matches) {
        this.clearProductMotion();
        return;
      }

      const travel = Math.max(1, viewportHeight - stickyTop);
      const viewportCenter = stickyTop + (travel / 2);

      this.items.forEach((item, index) => {
        const panels = this.itemPanels[index];
        if (!panels.length) return;

        let relativePosition;
        if (continuousIndex === null) {
          const rect = rects[index];
          relativePosition = ((rect.top + (rect.height / 2)) - viewportCenter) / travel;
        } else {
          relativePosition = index - continuousIndex;
        }

        const clampedPosition = Math.max(-1, Math.min(1, relativePosition));
        const offset = `${(clampedPosition * travel).toFixed(3)}px`;
        panels.forEach((panel) => panel.style.setProperty('--banner-sticky-product-scroll-y', offset));
      });
    }

    clearProductMotion() {
      this.itemPanels.forEach((panels) => panels.forEach((panel) => panel.style.removeProperty('--banner-sticky-product-scroll-y')));
    }

    scrollToIndex(index, immediate) {
      const item = this.items[index];
      if (!item) return;
      const stickyTop = this.getStickyTop();
      const trackTop = this.track.getBoundingClientRect().top + window.scrollY;
      const targetOffset = this.dataset.resolvedStyle === 'wipe'
        ? index * Math.max(1, this.wipeStep || window.innerHeight)
        : this.flowOffsets[index];
      const targetTop = trackTop + targetOffset - stickyTop;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: immediate || this.reducedMedia.matches ? 'auto' : 'smooth'
      });
      if (immediate || this.reducedMedia.matches) this.setActive(index, true);
    }

    // canReveal comes from the scroll read phase; other callers (block select, nav click) measure here.
    setActive(index, scrollNav, canReveal) {
      if (index < 0 || index >= this.items.length) return;
      const changed = this.activeIndex !== index;
      this.activeIndex = index;

      if (changed) {
        this.buttons.forEach((button, buttonIndex) => {
          button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
        });
        this.items.forEach((item, itemIndex) => {
          item.toggleAttribute('data-active', itemIndex === index);
          item.toggleAttribute('data-before-active', itemIndex < index);
          item.toggleAttribute('data-after-active', itemIndex > index);
        });
        if (scrollNav) this.revealActiveButton(index);
        this.syncVideos();
      }

      const reveal = canReveal === undefined
        ? this.canRevealItem(this.items[index].getBoundingClientRect(), this.getStickyTop(), window.innerHeight)
        : canReveal;
      this.syncProductTitleReveals(index, reveal);
    }

    syncProductTitleReveals(activeIndex, canReveal) {
      const headingReveal = window.AxiomHeadingReveal;
      if (!headingReveal) return;

      this.items.forEach((item, itemIndex) => {
        item.querySelectorAll('[data-banner-sticky-product-title]').forEach((title) => {
          const shouldReveal = canReveal && itemIndex === activeIndex;
          const isRevealed = title.dataset.headingRevealState === 'active';
          if (shouldReveal && !isRevealed) headingReveal.reveal(title);
          if (!shouldReveal && isRevealed) headingReveal.reset(title);
        });
      });
    }

    revealActiveButton(index) {
      if (!this.rail) return;
      const button = this.buttons[index];
      if (!button) return;

      if (!this.mobileMedia.matches) {
        const scroller = this.sidebarScroller;
        if (!scroller) return;
        const scrollerRect = scroller.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        if (buttonRect.top >= scrollerRect.top && buttonRect.bottom <= scrollerRect.bottom) return;
        const nextTop = scroller.scrollTop + buttonRect.top - scrollerRect.top - ((scrollerRect.height - buttonRect.height) / 2);
        scroller.scrollTo({ top: Math.max(0, nextTop), behavior: this.reducedMedia.matches ? 'auto' : 'smooth' });
        return;
      }

      const railRect = this.rail.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      if (buttonRect.left >= railRect.left && buttonRect.right <= railRect.right) return;
      const nextLeft = this.rail.scrollLeft + buttonRect.left - railRect.left - ((railRect.width - buttonRect.width) / 2);
      this.rail.scrollTo({ left: Math.max(0, nextLeft), behavior: this.reducedMedia.matches ? 'auto' : 'smooth' });
    }

    syncVideos() {
      this.items.forEach((item, index) => {
        const player = item.querySelector('.category-showcase__media theme-video');
        if (!player) return;
        if (this.isNearViewport && index === this.activeIndex && !this.reducedMedia.matches) player.play();
        else player.pause();
      });
    }

    pauseVideos() {
      this.items.forEach((item) => {
        const player = item.querySelector('.category-showcase__media theme-video');
        if (player) player.pause();
      });
    }
  }

  customElements.define('category-showcase', CategoryShowcase);
})();
